/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Unit holds — GET /re/unit-holds (status, project_id), POST /re/unit-holds/{id}/release.
 * "Convert" opens the booking wizard for the unit; the backend converts the hold on booking.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { usePermission } from 'auth/usePermission';
import RePage, { FilterBar, LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { RePicker, VocabSelect } from './components/Pickers';
import { Num } from './components/Widgets';
import {
  HOLD_STATUSES, formatDateTime, formatMoney, useReApi, useReList,
} from './api';
import { defaultCurrency } from '../variant';

const countdown = (expiresAt, now) => {
  const ms = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(ms)) return { text: '—', tone: null };
  if (ms <= 0) return { text: 'Expired', tone: '#BA1A1A' };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const text = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${String(m).padStart(2, '0')}m`;
  return { text, tone: h < 6 ? '#BA1A1A' : h < 24 ? '#8A5300' : '#1F8A56' };
};

export default function UnitHolds() {
  const navigate = useNavigate();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canCreate: canBook } = usePermission('re_bookings');
  const [status, setStatus] = useState('ACTIVE');
  const [projectId, setProjectId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(null);
  const list = useReList('/re/unit-holds', { status, project_id: projectId, sort_by: 'expires_at', sort_order: 'asc' });

  // One ticker for every countdown on the page.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const release = async (h) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Release the hold on unit ${h.unit_number || h.unit_id}? The unit becomes available again.`)) return;
    setBusy(h.id);
    try {
      await api.post(`/re/unit-holds/${h.id}/release`);
      notify('Hold released');
      list.reload();
    } catch (e) {
      notify.error(e, 'Could not release the hold');
    } finally {
      setBusy(null);
    }
  };

  return (
    <RePage title="Unit holds" subtitle="Units reserved for a buyer before booking (EOI)">
      <FilterBar>
        <VocabSelect label="Status" value={status} onChange={setStatus} values={HOLD_STATUSES} />
        <RePicker path="/re/projects" label="Project" value={projectId} onChange={setProjectId} />
      </FilterBar>
      <LoadError error={list.error} onRetry={list.reload} />
      <RePanel p={0}>
        <ReTable
          loading={list.loading}
          rows={list.items}
          emptyMessage="No holds."
          total={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          columns={[
            { key: 'unit_number', label: 'Unit', render: (h) => <strong>{h.unit_number || `#${h.unit_id}`}</strong> },
            { key: 'project_name', label: 'Project' },
            { key: 'contact_name', label: 'Buyer', render: (h) => h.contact_name || h.lead_name || '—' },
            { key: 'broker_firm_name', label: 'Broker', render: (h) => h.broker_firm_name || '—' },
            { key: 'held_by_name', label: 'Held by', render: (h) => h.held_by_name || h.created_by_name || '—' },
            { key: 'eoi_amount', label: 'EOI', align: 'right', render: (h) => <Num>{formatMoney(h.eoi_amount, defaultCurrency(), { decimals: 0 })}</Num> },
            { key: 'expires_at', label: 'Expires', render: (h) => formatDateTime(h.expires_at) },
            {
              key: 'countdown', label: 'Time left',
              render: (h) => {
                if (h.status !== 'ACTIVE') return '—';
                const c = countdown(h.expires_at, now);
                return <Num bold color={c.tone}>{c.text}</Num>;
              },
            },
            { key: 'status', label: 'Status', render: (h) => <ReStatusChip vocab="hold" status={h.status} size="xs" /> },
            {
              key: 'actions', label: '', align: 'right',
              render: (h) => (h.status === 'ACTIVE' && canBook ? (
                <MDBox display="flex" gap={0.5} justifyContent="flex-end">
                  <MDButton size="small" variant="text" color="info" onClick={() => navigate(`/real-estate/bookings/new?unit_id=${h.unit_id}${h.lead_id ? `&lead_id=${h.lead_id}` : ''}`)}>
                    Convert to booking
                  </MDButton>
                  <MDButton size="small" variant="text" color="error" onClick={() => release(h)} disabled={busy === h.id}>Release</MDButton>
                </MDBox>
              ) : null),
            },
          ]}
        />
      </RePanel>
      {snack}
    </RePage>
  );
}
